import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import { getSnapshot, refreshSnapshot } from '@/data/client';
import type { SnapshotRef, SnapshotResponse } from '@/data/types';
import { SourceViewer } from '@/components/vault/SourceViewer';

/* The AI account snapshot. Every point carries chips for the inputs it rests on;
   a chip opens the Vault document, deal or contact. It refreshes itself in the
   background when the account's data changed. */
export function SnapshotCard({ companyId, onOpenVault }: { companyId: string; onOpenVault: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [viewing, setViewing] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ['snapshot', companyId],
    queryFn: () => getSnapshot(companyId),
    refetchInterval: (q) => (q.state.data?.updating ? 5000 : false),
  });
  const refresh = useMutation({
    mutationFn: () => refreshSnapshot(companyId),
    onSuccess: (r) => qc.setQueryData(['snapshot', companyId], r),
  });

  if (!data) return null;
  if (data.state === 'empty') {
    return (
      <div className="tr-card tr-card--dark snap snap--empty">
        <Head data={data} onRefresh={null} />
        <p>The snapshot appears once this account has some history: a note, a meeting, a deal, or documents in its <button type="button" className="snap__link" onClick={onOpenVault}>Client Vault</button>.</p>
      </div>
    );
  }
  if (data.state === 'unavailable') {
    return <div className="tr-card tr-card--dark snap snap--empty"><Head data={data} onRefresh={null} /><p>The snapshot needs the Anthropic API key on the server.</p></div>;
  }
  if (!data.snapshot) {
    return <div className="tr-card tr-card--dark snap snap--empty"><Head data={data} onRefresh={null} /><p>Reading the account… this takes about half a minute.</p></div>;
  }

  const s = data.snapshot;
  const chips = (ids: string[]) => ids.length > 0 && (
    <span className="snap__chips">
      {ids.map((id) => {
        const ref = data.refs[id];
        if (!ref) return null;
        const open = chipAction(ref, navigate, setViewing);
        return (
          <button key={id} type="button" className={`snap__chip snap__chip--${ref.type}`} title={ref.label} onClick={open ?? undefined} disabled={!open}>
            <Icon name={CHIP_ICON[ref.type]} size={10} />{ref.label.length > 28 ? `${ref.label.slice(0, 27)}…` : ref.label}
          </button>
        );
      })}
    </span>
  );
  const list = (title: string, items: { text: string; sources: string[]; extra?: string }[]) => items.length > 0 && (
    <div className="snap__sect">
      <h4>{title}</h4>
      <ul>{items.map((x, i) => <li key={i}><span>{x.extra && <em>{x.extra}</em>}{x.text}</span>{chips(x.sources)}</li>)}</ul>
    </div>
  );

  return (
    <div className="tr-card tr-card--dark snap">
      <Head data={data} onRefresh={() => refresh.mutate()} refreshing={refresh.isPending} />
      <p className="snap__headline">{s.headline}</p>
      <p className="snap__rel">{s.relationship}</p>
      {s.nextStep.text && (
        <div className="snap__next">
          <Icon name="arrow-right" size={16} />
          <div><strong>{s.nextStep.text}</strong><span>{s.nextStep.why}</span>{chips(s.nextStep.sources)}</div>
        </div>
      )}
      <div className="snap__grid">
        {list('What they buy', s.whatTheyBuy)}
        {list('Their goals', s.goals)}
        {list('Open commitments', s.commitments.map((c) => ({ ...c, extra: `${c.owner === 'us' ? 'Us' : 'Them'}${c.due ? ` · ${c.due}` : ''}: ` })))}
        {list('Risks', s.risks)}
        {list('Key dates', s.keyDates.map((d) => ({ ...d, extra: `${d.date}: ` })))}
        {s.gaps.length > 0 && <div className="snap__sect"><h4>Missing</h4><ul>{s.gaps.map((g, i) => <li key={i}><span>{g}</span></li>)}</ul></div>}
      </div>
      {viewing && <SourceViewer sourceId={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function Head({ data, onRefresh, refreshing }: { data: SnapshotResponse; onRefresh: (() => void) | null; refreshing?: boolean }) {
  return (
    <div className="dash-ai__head snap__head">
      <span className="dash-ai__icon"><Icon name="sparkles" size={18} /></span>
      <h2>Account snapshot</h2>
      <span className="snap__meta">
        {data.updating ? 'Updating with the latest activity…' : data.generatedAt ? `Updated ${relativeTime(data.generatedAt)}` : ''}
        {data.error && !data.updating ? ' · last update failed' : ''}
      </span>
      {onRefresh && (
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={refreshing || data.updating} iconLeft={<Icon name="refresh-cw" size={13} />}>Refresh</Button>
      )}
    </div>
  );
}

const CHIP_ICON: Record<SnapshotRef['type'], string> = {
  company: 'layers', contact: 'users', deal: 'dollar', meeting: 'calendar', activity: 'clock', vault: 'lock',
};

function chipAction(ref: SnapshotRef, navigate: (to: string) => void, view: (id: string) => void): (() => void) | null {
  if (ref.type === 'vault') return () => view(ref.id);
  if (ref.type === 'deal') return () => navigate(`/deals/${ref.id}`);
  if (ref.type === 'contact') return () => navigate(`/contacts/${ref.id}`);
  return null; // company, meeting, timeline entry: the label says enough
}
